/**
 * MobX store for one RMA timeline row: metadata, optional return_error, CloudWatch
 * logs action, and derived classified-error presentation (priority among non-generic rows).
 */
import {makeAutoObservable} from "mobx";
import {
    IClassifiedError,
    ITimelineItemDto,
    RMA_EXTERNAL_TRANSACTION_STATUS,
    TIMELINE_ITEM_TYPE_ENUM,
    IRmaError,
} from "../rma-types";
import {rootContainer, TYPE} from "../../../../services/container/root";
import TroubleshootingService from "../../../../services/api/troubleshooting-service";

class TimelineItemStore {
    private _model: ITimelineItemDto = {} as ITimelineItemDto;
    constructor(model: ITimelineItemDto) {
        makeAutoObservable(this, {}, {autoBind: true});
        this._setModel(model);
    }
    
    private _setModel(model: ITimelineItemDto){
        this._model = model;
    }
    
    public get type(): TIMELINE_ITEM_TYPE_ENUM { return this._model.type; }
    public get content(): string { return this._model.content; }
    public get isSystem(): boolean { return this._model.is_system; }
    public get isAutomation(): boolean { return this._model.is_automation; }
    public get userName(): string { return this._model.user_name; }
    public get createdAt(): string { return this._model.created_at; }
    public get platform(): string { return this._model.transaction_data?.platform ?? ''; }
    public get status(): RMA_EXTERNAL_TRANSACTION_STATUS | undefined { return this._model.transaction_data?.status; }
    public get transactionId(): number | undefined { return this._model.transaction_data?.transaction_id; }
    public get returnError(): IRmaError | null { return this._model.return_error; }

    /**
     * Plain-text segments for the timeline message body. Error rows split on `##`; other types
     * are a single segment. Safe to render in JSX (no HTML assembly).
     */
    public get timelineMessageBodyLines(): string[] {
        if (this.type === TIMELINE_ITEM_TYPE_ENUM.TYPE_ERROR) {
            return this.content.split('##');
        }
        return [this.content];
    }

    /**
     * When a non-generic classified row exists, returns props for the classified timeline
     * layout; otherwise null (caller uses raw content).
     */
    public get classifiedErrorTimelineView(): {
        returnError: IRmaError;
        highestPriorityClassifiedError: IClassifiedError;
        rawContentLines: string[];
    } | null {
        if (!this.returnError) {
            return null;
        }
        const highestPriorityClassifiedError =
            this._pickHighestPriorityNonGenericClassifiedError(
                this.returnError.classified_errors,
            );
        if (!highestPriorityClassifiedError) {
            return null;
        }
        return {
            returnError: this.returnError,
            highestPriorityClassifiedError,
            rawContentLines: this.timelineMessageBodyLines,
        };
    }

    public async generateLogsUrl(rmaId: number): Promise<void> {
        try {
            const troubleshootingService = rootContainer.get<TroubleshootingService>(TYPE.TroubleshootingService);
            const filters: Record<string, string> = {};

            // Add available filters based on what we have
            filters.rma_id = rmaId.toString();
            // Note: case_id and order_id would need to be added to TimelineItemStore if available

            /** Create Date object only for time calculations */
            const createdAtDate = new Date(this.createdAt);
            const startTime = new Date(createdAtDate.getTime() - 60000); /** -1 minute */
            const endTime = new Date(createdAtDate.getTime() + 60000); /** +1 minute */
            const url = await troubleshootingService.generateLogsPageUrl(
                startTime.toISOString().slice(0, -5) + 'Z',
                endTime.toISOString().slice(0, -5) + 'Z',
                filters,
            );

            console.info('Opening logs URL:', url);

            // Open in new tab
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (error) {
            console.error('Failed to generate logs URL:', error);
        }
    }

    private _pickHighestPriorityNonGenericClassifiedError(
        rows: IClassifiedError[] | null | undefined,
    ): IClassifiedError | null {
        if (!rows?.length) {
            return null;
        }
        /** Generic classifier uses code `GEN_001`; anything else counts as specific (including missing code). */
        const nonGeneric = rows.filter((r) => r.code !== 'GEN_001');
        if (!nonGeneric.length) {
            return null;
        }
        nonGeneric.sort((a, b) => {
            if (b.weight !== a.weight) {
                return b.weight - a.weight;
            }
            return a.id - b.id;
        });
        return nonGeneric[0] ?? null;
    }

}

export default TimelineItemStore;
